import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Button,
  useTheme,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { PageLayout } from '../../components/footer/PageLayout';
import { ContentSection } from '../../components/footer/ContentSection';
import { FAQSection } from '../../components/footer/FAQSection';
import { CTASection } from '../../components/footer/CTASection';

export const HelpPage: React.FC = () => {
  const theme = useTheme();

  const faqs = [
    {
      question: "How do I find a caregiver?",
      answer: "Use our search tool to find caregivers by location, specialty, and availability. You can filter results and read reviews to find the best match for your needs.",
    },
    {
      question: "How do I book an appointment?",
      answer: "Once you find a caregiver, click on their profile to view availability and book an appointment. You can choose a date and time that works for both of you.",
    },
    {
      question: "What if I need to cancel an appointment?",
      answer: "You can cancel or reschedule appointments through your account dashboard. Please provide at least 24 hours notice when possible.",
    },
    {
      question: "How do I pay for services?",
      answer: "We accept most major insurance plans, HSA/FSA cards, and credit/debit cards. Payment is processed securely through our platform.",
    },
    {
      question: "What if I have a complaint or concern?",
      answer: "We take all feedback seriously. You can contact our support team or use our reporting system to address any concerns.",
    },
  ];

  const supportOptions = [
    {
      title: "Email Support",
      description: "Send us a message at support@careva.ai and we'll respond within 24 hours",
      icon: "📧",
      action: "mailto:support@careva.ai",
    },
    {
      title: "Help Center",
      description: "Browse our comprehensive help articles and FAQs",
      icon: "📚",
      action: "/help",
    },
    {
      title: "Contact Form",
      description: "Fill out our contact form for detailed inquiries",
      icon: "📝",
      action: "/contact",
    },
    {
      title: "Report an Issue",
      description: "Report safety concerns or platform issues",
      icon: "🚨",
      action: "/report",
    },
  ];

  return (
    <PageLayout
      title="Help Center"
      subtitle="Find answers to common questions and get the support you need."
    >
      {/* Support Options */}
      <ContentSection
        title="How Can We Help?"
        subtitle="Choose the best way to get support based on your needs."
        backgroundColor={theme.palette.grey[50]}
      >
        <Grid container spacing={4}>
          {supportOptions.map((option, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box
                component={option.action?.startsWith('mailto:') ? 'a' : RouterLink}
                to={option.action?.startsWith('/') ? option.action : undefined}
                href={option.action?.startsWith('mailto:') ? option.action : undefined}
                sx={{
                  textAlign: 'center',
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'block',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    transition: 'transform 0.2s',
                  },
                }}
              >
                <Box
                  sx={{
                    fontSize: '3rem',
                    mb: 2,
                  }}
                >
                  {option.icon}
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    mb: 1,
                    color: theme.palette.text.primary,
                  }}
                >
                  {option.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.6,
                  }}
                >
                  {option.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </ContentSection>

      {/* FAQ */}
      <ContentSection
        title="Frequently Asked Questions"
        subtitle="Find answers to the most common questions about our platform and services."
      >
        <FAQSection faqs={faqs} />
      </ContentSection>

      {/* CTA Section */}
      <CTASection
        title="Still Need Help?"
        subtitle="Our support team is here to help. Contact us and we'll get back to you as soon as possible."
        primaryButton={{
          text: "Contact Support",
          href: "/contact",
        }}
        secondaryButton={{
          text: "Email Support",
          href: "mailto:support@careva.ai",
          variant: "outlined",
        }}
      />
    </PageLayout>
  );
};
